import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import * as React from "react";
/* Custom Code */

import usePrevious from "@insite/client-framework/Common/Hooks/usePrevious";
import { HasCategoryContext, withCategory } from "@insite/client-framework/Components/CategoryContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSelectedCategoryPath } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCatalogPageStateByPath } from "@insite/client-framework/Store/Data/CatalogPages/CatalogPagesSelectors";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";

import translate from "@insite/client-framework/Translate";
import { useLocation } from "@store/Framework/Store/Data/Pages/PageHooks";
import Link from "@store/MobiusOverrides/Link/Link";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";
/* Custom Code */

interface Props extends WidgetProps, HasCategoryContext {}

const enum fields {
    layout = "layout",
    title = "title",
    description = "description",
    disclaimer = "disclaimer",
    buttonLabel = "buttonLabel",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.layout]: string;
        [fields.title]: string;
        [fields.description]: string;
        [fields.disclaimer]: string;
        [fields.buttonLabel]: string;
        [fields.customCSS]: string;
    };
}

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
    dataTestSelector: "AdvancedTab",
};

export interface CategoryDetailBackLinkStyles {
    container?: InjectableCss;
}

export const bannerStyles: CategoryDetailBackLinkStyles = {};

const styles = bannerStyles;

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const categoryPath =
        getSelectedCategoryPath(state) ||
        (location.pathname.toLowerCase().startsWith("/content/") ? "" : location.pathname);
    const catalogPageState = getCatalogPageStateByPath(state, categoryPath);
    const {
        pages: {
            productList: { isSearchPage, isLoading },
        },
    } = state;

    return {
        isSearchPageLoading: isLoading,
        isSearchPage,
        breadcrumbLinks: state.components.breadcrumbs.links,
        catalogPageState,
        properties: catalogPageState.value?.properties,
    };
};

const mapDispatchToProps = {};

const getPreviousUrl = (currentPath: string) => {
    const pathArray = currentPath.split("/");
    const newPath = pathArray.slice(0, -1).join("/");
    return newPath !== "" ? newPath : undefined;
};

const defaultCustomCss = `.category-detail-backlink {
    cursor: pointer;
    font-family: inherit;
    backface-visibility: hidden;
    word-break: keep-all;
    padding: 0rem 1rem;
    border-radius: 0.25rem;
    background: #00305e;
    border: none;
    color: #ffffff;
    font-size: 16px;
    min-height: 45px;
    min-width: 120px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-weight: 700;
    line-height: 1.25;
    transition: all 250msease-in-out;
}
.category-detail-backlink:hover {
    text-decoration: none;
    border: none;
    color: white;
    background: #3da7d3;
}`;
const CategoryDetailBackLink = ({
    breadcrumbLinks,
    fields: ownFields,
    isSearchPage,
    isSearchPageLoading,
    ...props
}: Props & OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>) => {
    const location = useLocation();
    const previousUrlPath = getPreviousUrl(location.pathname);
    const previousCategoryLink = breadcrumbLinks?.find(x => x.href === previousUrlPath);
    const backTo = translate("Back To");
    const customCssWrapper = {
        css: css`
            ${ownFields.customCSS}
        `,
    };
    if (isSearchPageLoading || isSearchPage || !previousCategoryLink) {
        return null;
    }
    return (
        <StyledWrapper {...customCssWrapper} data-test-selector="CategoryDetailBackLink">
            <Link
                className="category-detail-backlink"
                href={previousCategoryLink.href}
            >{`${backTo} ${previousCategoryLink?.children}`}</Link>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withCategory(CategoryDetailBackLink)),
    definition: {
        group: "Category Details",
        icon: "link",
        allowedContexts: ["CategoryDetailsPage", "ProductListPage"],
        fieldDefinitions: [
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => true,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
